import { useEffect, useRef, useState } from "react";
import { Upload, Image as ImageIcon, Loader2, Lock, Check, User, PawPrint, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { StyleId, STYLES } from "./StyleSelector";
import { CheckoutDialog } from "./CheckoutDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import heic2any from "heic2any";
import { Pack, Mode, getPrice, MODE_LABEL_KEY, MODE_DESC_KEY } from "@/lib/packPricing";
import { addWatermark } from "@/lib/watermark";
import { trackEvent } from "@/lib/track";

type Demo = { style: StyleId; image: string };

const MODES: { id: Mode; icon: typeof User }[] = [
  { id: "portrait", icon: User },
  { id: "pet", icon: PawPrint },
];

const ALL_STYLES: StyleId[] = STYLES.map((s) => s.id);

const SUPPORTED_EXTS = ["jpg", "jpeg", "png", "heic", "heif", "webp", "bmp", "tiff", "tif", "avif"];

function getFileExt(file: File): string {
  return (file.name.split(".").pop() || "").toLowerCase();
}

function isSupported(file: File): boolean {
  const ext = getFileExt(file);
  if (SUPPORTED_EXTS.includes(ext)) return true;
  const type = file.type.toLowerCase();
  return type.startsWith("image/");
}

const MAX_DIMENSION = 1000;
const JPEG_QUALITY = 0.75;

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      // keep url alive until after draw; revoke in caller
      (img as any)._objectUrl = url;
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
}

async function blobToJpegDataUrl(blob: Blob): Promise<string> {
  const img = await loadImageFromBlob(blob);
  try {
    const longest = Math.max(img.width, img.height);
    const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    // White background so transparent PNGs don't become black on JPEG
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const dataUrl: string = await new Promise((resolve, reject) => {
      if (canvas.toBlob) {
        canvas.toBlob(
          (out) => {
            if (!out) return reject(new Error("Canvas conversion failed"));
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error("FileReader failed"));
            reader.readAsDataURL(out);
          },
          "image/jpeg",
          JPEG_QUALITY
        );
      } else {
        // Safari fallback
        try {
          resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
        } catch (err) {
          reject(err as Error);
        }
      }
    });
    return dataUrl;
  } finally {
    const url = (img as any)._objectUrl as string | undefined;
    if (url) URL.revokeObjectURL(url);
  }
}

async function convertToJpeg(file: File): Promise<string> {
  const ext = getFileExt(file);
  const type = file.type.toLowerCase();

  // HEIC / HEIF → decode via heic2any first, then run through canvas pipeline
  if (ext === "heic" || ext === "heif" || type.includes("heic") || type.includes("heif")) {
    const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
    const jpegBlob = Array.isArray(result) ? result[0] : result;
    return blobToJpegDataUrl(jpegBlob as Blob);
  }

  // Everything else (JPEG, PNG, WEBP, AVIF, BMP, TIFF) → canvas resize + JPEG compress
  return blobToJpegDataUrl(file);
}

export const UploadFlow = ({ style }: { style: StyleId }) => {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [demos, setDemos] = useState<Demo[]>([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [pack, setPack] = useState<Pack>("single");
  const [mode, setMode] = useState<Mode>("portrait");
  const [selectedStyles, setSelectedStyles] = useState<StyleId[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Backwards compatibility: detail may be a string or {pack, mode}
      const nextPack = typeof detail === "string" ? detail : detail?.pack;
      const nextMode = typeof detail === "object" ? detail?.mode : undefined;
      if (nextPack === "single" || nextPack === "triple" || nextPack === "gift") {
        setPack(nextPack);
        setSelectedStyles([]);
        setDemos([]);
      }
      if (nextMode === "portrait" || nextMode === "scene" || nextMode === "pet") {
        setMode(nextMode);
      }
    };
    window.addEventListener("select-pack", handler);
    return () => window.removeEventListener("select-pack", handler);
  }, []);

  const handleFile = async (file: File) => {
    if (!isSupported(file)) {
      toast({ title: "Unsupported file", description: "Please upload a valid image file.", variant: "destructive" });
      return;
    }
    try {
      const dataUrl = await convertToJpeg(file);
      setPhoto(dataUrl);
      setDemos([]);
    } catch (e) {
      console.error(e);
      toast({ title: "Conversion failed", description: "Could not process this image. Try another format.", variant: "destructive" });
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setDemos([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const toggleStyle = (id: StyleId) => {
    setSelectedStyles((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const stylesToGenerate = (): StyleId[] => {
    if (pack === "single") return [style];
    if (pack === "triple") return selectedStyles;
    if (mode === "pet") return ALL_STYLES;
    return ALL_STYLES.filter((s) => s !== "caricature");
  };

  const canGenerate = () => {
    if (!photo || generating) return false;
    if (pack === "triple" && selectedStyles.length !== 3) return false;
    return true;
  };

  const generate = async () => {
    if (!photo) return;
    const list = stylesToGenerate();
    setGenerating(true);
    setDemos([]);
    setProgress({ done: 0, total: list.length });
    try {
      const results: Demo[] = [];
      for (let i = 0; i < list.length; i++) {
        const s = list[i];
        const { data, error } = await supabase.functions.invoke("generate-cartoon", {
          body: { image: photo, style: s, mode },
        });
        if (error) throw error;
        if (!data?.image) throw new Error("No image returned");
        const watermarked = await addWatermark(data.image as string);
        results.push({ style: s, image: watermarked });
        setDemos([...results]);
        setProgress({ done: i + 1, total: list.length });
      }
    } catch (e: any) {
      console.error(e);
      const msg = e?.context?.status === 429
        ? "Too many requests. Please wait a moment and try again."
        : e?.context?.status === 402
          ? "AI credits exhausted. Please add credits in Lovable Cloud."
          : e?.message || "Generation failed. Please try again.";
      toast({ title: "Generation failed", description: msg, variant: "destructive" });
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  };

  const saveImage = async (src: string, filename: string) => {
    // Fetch as blob (handles data URLs and remote URLs)
    const res = await fetch(src);
    const blob = await res.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

    // 1) Try Web Share API with files (best on iOS Safari — saves to Photos via share sheet)
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
    if (isIOS && nav.canShare && nav.canShare({ files: [file] }) && nav.share) {
      try {
        await nav.share({ files: [file], title: filename });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
      }
    }

    // 2) Try blob URL with <a download> (works on Android Chrome, Samsung Browser, desktop)
    const blobUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      const supportsDownload = "download" in a;
      a.click();
      document.body.removeChild(a);

      if (!supportsDownload) {
        // 3) Fallback: Web Share API (any platform)
        if (nav.canShare && nav.canShare({ files: [file] }) && nav.share) {
          try {
            await nav.share({ files: [file], title: filename });
            return;
          } catch (err) {
            if ((err as DOMException)?.name === "AbortError") return;
          }
        }
        // 4) Last resort: open in new tab so user can long-press / save
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      }
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }
  };

  const downloadTest = async () => {
    // Direct download each image via blob URL + <a download> — no share dialog.
    for (let i = 0; i < demos.length; i++) {
      const d = demos[i];
      try {
        const res = await fetch(d.image);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        try {
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `punchink-${d.style}-${i + 1}.jpg`;
          a.rel = "noopener";
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } finally {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        }
        // Small delay so browsers don't block multiple downloads
        if (i < demos.length - 1) {
          await new Promise((r) => setTimeout(r, 300));
        }
      } catch (err) {
        console.error("Failed to download image", err);
      }
    }
  };

  const packPrice = getPrice(pack, mode);

  return (
    <section id="upload" className="py-20 relative">
      <div className="container max-w-5xl">
        <div className="text-center mb-8 space-y-3">
          <h2 className="font-display text-4xl md:text-5xl font-black">
            <span className="text-gradient">{t("upload.title")}</span>
          </h2>
          <p className="text-muted-foreground text-lg">{t("upload.subtitle")}</p>
        </div>

        {/* Mode selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-2">
          {MODES.map(({ id, icon: Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                onClick={() => { setMode(id); setDemos([]); }}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition",
                  active
                    ? "border-neon-green text-neon-green glow-green bg-neon-green/10"
                    : "border-border text-muted-foreground hover:border-neon-purple/60"
                )}
              >
                <Icon className="w-4 h-4" />
                {t(MODE_LABEL_KEY[id])}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground mb-6">{t(MODE_DESC_KEY[mode])}</p>

        {/* Pack selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {(["single", "triple", "gift"] as Pack[]).map((p) => (
            <button
              key={p}
              onClick={() => { setPack(p); setSelectedStyles([]); setDemos([]); }}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-bold border-2 transition",
                pack === p
                  ? "border-neon-green text-neon-green glow-green bg-neon-green/10"
                  : "border-border text-muted-foreground hover:border-neon-purple/60"
              )}
            >
              {t(`pack.${p}`)} — {getPrice(p, mode)}
            </button>
          ))}
        </div>

        {/* Triple pack: pick 3 styles */}
        {pack === "triple" && (
          <div className="mb-6">
            <p className="text-center text-sm text-muted-foreground mb-3">
              {t("pack.pickStyles").replace("{n}", "3")} ({selectedStyles.length}/3)
            </p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 max-w-3xl mx-auto">
              {STYLES.filter((s) => mode === "pet" || s.id !== "caricature").map((s) => {
                const active = selectedStyles.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleStyle(s.id)}
                    className={cn(
                      "relative aspect-square rounded-xl overflow-hidden border-2 transition",
                      active ? "border-neon-green scale-[1.03]" : "border-border hover:border-neon-purple/60 opacity-70"
                    )}
                  >
                    <img src={s.image} alt={t(`style.${s.id}`)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-1 left-1 right-1 text-[10px] font-bold text-left">{t(`style.${s.id}`)}</div>
                    {active && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-neon-green grid place-items-center text-background">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {pack === "gift" && (
          <p className="text-center text-sm text-neon-green mb-6 font-semibold">{t("pack.allStyles")}</p>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Upload area */}
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            className="aspect-square rounded-2xl border-2 border-dashed border-neon-purple/40 bg-card/50 hover:border-neon-purple cursor-pointer transition-all overflow-hidden relative group"
          >
            {photo ? (
              <div className="relative w-full h-full">
                <img src={photo} alt="Your upload" className="w-full h-full object-cover" />
                <button
                  onClick={(e) => { e.stopPropagation(); clearPhoto(); }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border border-border text-foreground hover:bg-destructive hover:text-destructive-foreground transition grid place-items-center"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="absolute inset-0 grid place-items-center text-center p-6">
                <div className="space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-full bg-neon-purple/10 grid place-items-center group-hover:bg-neon-purple/20 transition">
                    <Upload className="w-7 h-7 text-neon-purple" />
                  </div>
                  <div className="font-semibold">{t("upload.cta")}</div>
                  <div className="text-xs text-muted-foreground">{t("upload.formats")}</div>
                </div>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.bmp,.tiff,.tif,.avif,image/*"
              hidden
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>

          {/* Preview area */}
          <div className="aspect-square rounded-2xl border border-border bg-card/50 overflow-hidden relative">
            {demos.length === 0 && !generating && (
              <div className="absolute inset-0 grid place-items-center text-muted-foreground text-center p-6">
                <div className="space-y-2">
                  <ImageIcon className="w-10 h-10 mx-auto opacity-40" />
                  <div className="text-sm">
                    {pack === "single" ? t(`style.${style}`) : t(`pack.${pack}`)}
                  </div>
                </div>
              </div>
            )}
            {generating && (
              <div className="absolute inset-0 grid place-items-center bg-background/80">
                <div className="text-center space-y-3">
                  <Loader2 className="w-10 h-10 mx-auto animate-spin text-neon-green" />
                  <div className="font-semibold">{t("upload.generating")}</div>
                  {progress && (
                    <div className="text-sm text-muted-foreground">{progress.done} / {progress.total}</div>
                  )}
                </div>
              </div>
            )}
            {demos.length > 0 && !generating && (
              <div className={cn(
                "w-full h-full grid",
                demos.length === 1 ? "grid-cols-1" : demos.length <= 3 ? "grid-cols-3" : "grid-cols-3 grid-rows-2"
              )}>
                {demos.map((d, i) => (
                  <div key={i} className="relative overflow-hidden">
                    <img src={d.image} alt={t(`style.${d.style}`)} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    <div
                      aria-hidden
                      className="pointer-events-none absolute inset-0"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;utf8,${encodeURIComponent(
                          `<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><g transform='translate(120 120) rotate(-45)'><text text-anchor='middle' dominant-baseline='middle' fill='white' fill-opacity='0.4' font-family='system-ui,-apple-system,Segoe UI,Roboto,sans-serif' font-size='22' font-weight='700' style='paint-order:stroke;stroke:rgba(0,0,0,0.4);stroke-width:1'>PunchInk.com</text></g></svg>`
                        )}")`,
                        backgroundRepeat: "repeat",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <Button
            disabled={!canGenerate()}
            onClick={() => { trackEvent("generate-demo"); generate(); }}
            size="lg"
            className="bg-neon-green text-background hover:bg-neon-green/90 font-bold h-12 px-8"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("upload.generate")}
          </Button>
          {demos.length > 0 && !generating && (
            <>
              <p className="text-sm text-muted-foreground text-center">{t("upload.demoReady")}</p>
              <Button
                onClick={() => setCheckoutOpen(true)}
                size="lg"
                variant="outline"
                className="border-neon-purple text-neon-purple hover:bg-neon-purple/10 h-12 px-8 font-bold"
              >
                <Lock className="w-4 h-4" /> {t("pricing.buy")} — {packPrice}
              </Button>
              {/* TEMP: Download test button — remove before launch */}
              <Button
                onClick={downloadTest}
                size="lg"
                variant="outline"
                className="border-neon-green text-neon-green hover:bg-neon-green/10 h-12 px-8 font-bold"
              >
                <Download className="w-4 h-4" /> Download Test
              </Button>
            </>
          )}
        </div>
      </div>

      <CheckoutDialog
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        demos={demos}
        price={packPrice}
      />
    </section>
  );
};
